<template>
	<div class="kia-flat-rate-import-view">
		<SectionHeader
			class="kia-flat-rate-import-view__title"
			sectionText="Import"
			variant="defaultBg2"
			hasBorder
		/>
		<div class="kia-flat-rate-import-view__container">
			<div class="kia-flat-rate-import-view__defect-labor-container">
				<div class="kia-flat-rate-import-view__labor-container">
					<span class="kia-flat-rate-import-view__labor-title"
						>Labor Codes</span
					>
					<ul
						v-if="laborCodes.length !== 0"
						class="kia-flat-rate-import-view__items"
					>
						<li
							class="kia-flat-rate-import-view__item"
							v-for="laborCode in laborCodes"
							:key="laborCode.id"
						>
							<Card
								class="kia-flat-rate-import-view__card"
								variant="defaultBg1"
							>
								<template slot="card-body">
									<div
										class="kia-flat-rate-import-view__labor-code-container"
									>
										<div
											class="kia-flat-rate-import-view__labor-code-left"
										>
											<span
												class="kia-flat-rate-import-view__labor"
												data-testid="laborCode-value"
												>{{ laborCode.laborOp }}
											</span>
											<div
												class="kia-flat-rate-import-view__time-container"
											>
												<span
													class="kia-flat-rate-import-view__time-label"
													>Time:</span
												>

												<span
													class="kia-flat-rate-import-view__time-value"
												>
													{{ laborCode.laborHours }}
												</span>
											</div>
										</div>
										<ButtonIcon
											class="kia-flat-rate-import-view__close-btn"
											iconClassName="icon-remove"
											data-testid="laborCode-btn"
											@click="
												onRemoveLaborCode(laborCode.id)
											"
										/>
									</div>
								</template>
							</Card>
						</li>
					</ul>
					<div
						class="kia-flat-rate-import-view__no-results"
						v-if="laborCodes.length === 0"
					>
						<NoResultsMessage
							class="results-not-found"
							:visible="laborCodes.length === 0"
							primaryTextCondensed="No Labor Code Selected"
							secondaryTextCondensed="Select a labor code to import."
						/>
					</div>
				</div>
			</div>
			<ButtonSet
				class="kia-flat-rate-import-view__btn-set"
				data-testid="set-btn"
			>
				<div
					class="kia-flat-rate-import-btn"
					@mouseenter="importToolTipVisible = true"
					@mouseleave="importToolTipVisible = false"
					data-testid="inport-btn-div"
				>
					<ButtonClassic
						variant="primary"
						labelText="Import"
						@click="onImport"
						:disabled="!validSelection"
						ref="errorTooltip"
					>
					</ButtonClassic>
				</div>
				<ButtonClassic
					variant="text"
					labelText="Cancel"
					@click="onCancel"
					data-testid="cancel-btn"
				/>
			</ButtonSet>
			<Tooltip
				:visible="!validSelection && importToolTipVisible"
				state="negative"
				placement="top"
				trackedRefName="errorTooltip"
				positionStrategy="fixed"
				:offset="[0, 8]"
			>
				Select a code to import
			</Tooltip>
		</div>
	</div>
</template>

<script>
import ButtonClassic from '@/components/forms/button/button-classic.vue';
import NoResultsMessage from '@/components/shells/no-results-message/no-results-message.vue';
import Tooltip from '@/components/shells/tooltip/tooltip.vue';
import ButtonSet from '@/components/forms/button/button-set.vue';
import SectionHeader from '@/components/typography/section-header/section-header.vue';
import Card from '@/components/shells/card/card.vue';
import ButtonIcon from '@/components/forms/button/button-icon.vue';

export default {
	name: 'KiaFlatRateImport',
	components: {
		NoResultsMessage,
		ButtonClassic,
		Tooltip,
		ButtonSet,
		SectionHeader,
		Card,
		ButtonIcon
	},
	data() {
		return {
			importToolTipVisible: false
		};
	},
	props: {
		laborCodes: {
			type: Array,
			default: null
		}
	},
	computed: {
		validSelection() {
			return this.laborCodes?.length > 0;
		}
	},
	methods: {
		onRemoveLaborCode(id) {
			this.$emit('removeLaborCode', id);
		},
		onImport() {
			this.$emit('import');
		},
		onCancel() {
			this.$emit('cancel');
		}
	}
};
</script>

<style lang="scss" scoped>
.kia-flat-rate-import-view {
	display: flex;
	flex-direction: column;
	height: 100%;

	&__container {
		padding: $padding-md;
		display: flex;
		flex-direction: column;
		flex: 1;
		border: 1px solid var(--neutral-2);
		border-top: none;
	}

	&__defect-labor-container {
		display: flex;
		flex-direction: row;
		gap: $gutter-xxxl;
		flex: 1;
		overflow: hidden;

		@include mq($from: lg) {
			display: flex;
			flex-direction: column;
			gap: $gutter-xl;
		}
	}

	&__defect-title {
		@include heading(md);
	}

	&__labor-title {
		@include heading(md);

		padding: $padding-xs 0;
	}

	&__defect-container,
	&__labor-container {
		display: flex;
		flex-direction: column;
		gap: $gutter-lg;
		width: 100%;
		flex: 1;
		overflow: hidden;
	}

	&__defect-title-container {
		display: flex;
		align-items: center;
		gap: $gutter-sm;
	}

	&__info-btn {
		font-size: $heading-font-size-md;
	}

	&__defect-code-container {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	&__defect-code {
		font-weight: $bold-font-weight;
	}

	&__items {
		display: flex;
		flex-direction: column;
		gap: $gutter-md;
		@include scrollbar('interior-borderless');
		overflow-y: auto;
		flex: 1;
		min-height: 0;
		max-height: 325px;
	}

	&__labor-code-container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
	}

	&__labor-code-left {
		display: inline-flex;
		align-items: center;
		gap: $gutter-xl;
	}

	&__card {
		width: 100%;
	}

	&__labor {
		width: 120px;
		font-weight: $bold-font-weight;
	}

	&__time-label {
		color: var(--bg-1-fg-2);
	}

	&__close-btn {
		font-size: $card-icon-font-size;
	}

	&__no-results {
		border: 1px solid var(--neutral-2);
		padding: $padding-sm;
		width: 100%;
	}

	&__btn-set {
		justify-content: flex-end;
		margin-top: $gutter-xl;
	}
}

.results-not-found {
	margin-top: 0;
}

</style>
